import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { extractDate } from "@/utils/datetime"
import { useEventStore } from "@/stores/EventsStore"
import { generateUniqueId, getInitials } from "@/utils/strings"
import { useStorage } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useModalStore } from "@/stores/ModalStore"
import { useUserStore } from "@/stores/UserStore"
import { useRoute, useRouter } from "vue-router"
import { useCookie } from "@core/composable/useCookie"
import { useStackedActions } from "@/composables/useStackedActions"

export const useCalendarStore = defineStore('calendar', () => {
  /* ---------------------------- 👉 Translations ---------------------------- */
  const { t } = useI18n()
  const modalStore = useModalStore()
  const route = useRoute()
  const router = useRouter()

  const eventStore = useEventStore()
  const userStore = useUserStore()

  // Stacked actions — collect callbacks from components, run them via runActions()
  const stackedActions = useStackedActions()

  // State
  const isLoading = ref(true)

  const settingsLoaded = ref(false) // Used internally to determine if settings were already loaded

  const availableCoupons = ref([]) // Available coupons for the customer

  // Used for timepickers hours range
  const hoursRange = ref([7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21])

  // Terms and Agreement
  const terms = ref(false)
  const agreement = ref(false)

  // Used for storing settings from panel/settings/calendar/ (Ustawienia kalendarza)
  const viewSettings = ref({
    showWorkingHours: true, // Wyświetlaj godziny pracy w nagłówku pracownika
    eventPreview: true, // Wyświetlaj podgląd wizyty
    eventPreviewNotes: true, // Wyświetlaj podgląd wizyty - dołącz notatki
    eventPreviewClientNotes: true, // Wyświetlaj podgląd wizyty - dołącz opis klienta
    eventPreviewClientNoteOnCalendar: true, // Wyświetlaj notatkę o kliencie na widoku kalendarza
    eventPreviewDescriptionOnCalendar: true, // Wyświetlaj notatkę wizyty na widoku kalendarza
    showCanceledEvents: true, // Pokazuj domyślnie odwołane wizyty
    showDeletedEvents: true, // Pokazuj usunięte wizyty
    clientInSalonStatus: true, // Dodaj status wizyty "Klient w salonie"
    defaultView: "recepcja",
    initialView: "resourceTimeGridDay",
    editPastEvents: false,
    topMenuMessages: false,
    topMenuCheckout: false,
    topMenuLabelcall: false,
    allowClientsRemoval: false,
    showPanel10Button: false,
    showCalendarConflicts: true,
    showVisitsAndSalesOfAllEmployees: true,
    allowDeleteVisit: true,
    showUsedProductsValue: true,
    showRealEventDuration: false,
    portalPrepaymentEnabled: false,
    showProductStoreLink: false,
  })

  const integrations = ref({
    beautyDoc: {
      enabled: false,
      showBlockade: true,
    },
    wealthon: {
      enabled: false,
      showBlockade: true,
    },
  })

  // Used for showing menu in left sidebar
  const menuItems = ref({
    ambassador: false,
    beautyclub: false,
    calendar: true,
    clients: false,
    manager: false,
    marketing: false,
    messages: false,
    radio: false,
    settings: false,
    statistics: false,
    store: false,
    help: true,
  })

  const holidaysArray = ref({})

  const fetchedEvents = ref([])

  const fetchEmployeesAndResources = async (type = "", date = "") => {
    try {
      const queryParams = new URLSearchParams()
      if (type) queryParams.append('type', type)
      if (date) queryParams.append('date', date)

      const response = await $backend(`/calendar/resources?${queryParams.toString()}`, {
        method: 'GET',
      })

      // Add fallback color for employees
      employeesCalendars.value = response.data?.filter(item => isNaN(item.id)).map(employee => ({
        ...employee,
        color: employee.color,
      }))

      calendarEmployeesCalendars.value = response.data?.filter(item => isNaN(item.id) && item.showOnCalendar).map(employee => ({
        ...employee,
        color: employee.color,
      }))

      // Add fallback color for resources
      resourcesCalendars.value = response.data?.filter(item => !isNaN(item.id)).map(resource => ({
        ...resource,
        color: resource.color,
      }))

      return response.data

    } catch (error) {

    }

    return []

  }

  const clientGroups = ref([])

  const reasonsForEventCancellation = ref([ //B: i don't translate them couse they come from BE, this is just fallback
    { id: 0, text: "Bez podawania powodu" },
    { id: 1, text: "Klient uprzedził i odwołał" },
    { id: 2, text: "Klient przełożył wizytę na inny termin" },
    { id: 3, text: "Klient się nie zjawił" },
  ])

  /**
   * Fetches calendar settings from the backend.
   *
   * @async
   */
  const fetchCalendarSettings = async (force = false) => {
    if(settingsLoaded.value && !force) return
    try {
      isLoading.value = true
      const response = await $backend(`/calendar/settings`, {
        method: 'GET',
      })

      defaultSlotDuration.value = response.data.defaultSlotDuration
      defaultSlotDurationInMinutes.value = defaultSlotDuration.value.split(':').reduce((acc, val, i) => acc + Number(val) * (i === 0 ? 60 : 1), 0)
      calendarTimeFrom.value = response.data.calendarTimeFrom
      const [endHour, endMinute] = response.data.calendarTimeTo.split(':').map(Number)
      const endTimeInMinutes = endHour * 60 + endMinute + defaultSlotDurationInMinutes.value
      const newEndHour = Math.floor(endTimeInMinutes / 60)
      const newEndMinute = endTimeInMinutes % 60
      calendarTimeTo.value = `${String(newEndHour).padStart(2, '0')}:${String(newEndMinute).padStart(2, '0')}`
      currency.value = response.data.currency
      smsDomesticPrice.value = response.data.sms?.price
      smsInternationalPrice.value = response.data.sms?.internationalPrice

      viewSettings.value.calendarReadOnly = response.data.calendarReadOnly === "1" || response.data.calendarReadOnly === true
      viewSettings.value.showWorkingHours = response.data.showWorkingHours === "1"
      viewSettings.value.eventPreview = response.data.eventPreview === "1"
      viewSettings.value.eventPreviewNotes = response.data.eventPreviewNotes === "1"
      viewSettings.value.eventPreviewClientNotes = response.data.eventPreviewClientNotes === "1"
      viewSettings.value.eventPreviewClientNoteOnCalendar = response.data.eventPreviewClientNoteOnCalendar === "1"
      viewSettings.value.eventPreviewDescriptionOnCalendar = response.data.eventPreviewDescriptionOnCalendar === "1"
      viewSettings.value.showCanceledEvents = response.data.showCanceledEvents === "1"
      viewSettings.value.showDeletedEvents = response.data.showDeletedEvents === "1"
      viewSettings.value.clientInSalonStatus = response.data.clientInSalonStatus === "1"
      viewSettings.value.editPastEvents = response.data.editPastEvents === "1"
      viewSettings.value.initialView = ({
        recepcja: "resourceTimeGridDay",
        agendaWeek: "timeGridWeek",
        month: "dayGridMonth",
      })[response.data.defaultView] || "resourceTimeGridDay"
      //useStorage('initialView').value = viewSettings.value.defaultView
      //showCanceledEvents.value = viewSettings.value.showCanceledEvents //TODO: B jeszcze byc moze to zmienimy #869a26mk8
      viewSettings.value.topMenuMessages = response.data.topMenu.messages
      viewSettings.value.topMenuCheckout = response.data.topMenu.checkout
      viewSettings.value.topMenuLabelcall = response.data.topMenu.labelcall
      viewSettings.value.allowClientsRemoval = response.data.allowClientsRemoval
      viewSettings.value.requireRescheduleConfirm = response.data.requireRescheduleConfirm === "1" // Show confirmation after drag & drop on PC
      viewSettings.value.clientHasToConfirm = response.data.clientHasToConfirm === true // "Wymuszaj na kliencie potwierdzenie wizyty" -> couses extra label on events on FC
      viewSettings.value.eventPreviewFinalPrice = response.data.eventPreviewFinalPrice === "1" // If there should be information in tooltip about finalised event final price
      viewSettings.value.showPanel10Button = response.data.showPanel10Button === true
      viewSettings.value.showCalendarConflicts = response.data.showCalendarConflicts === true
      viewSettings.value.showCalendarConflicts = response.data.showCalendarConflicts === true
      viewSettings.value.showVisitsAndSalesOfAllEmployees = response.data.showVisitsAndSalesOfAllEmployees === true
      viewSettings.value.allowDeleteVisit = response.data.allowDeleteVisit === "1"
      viewSettings.value.showUsedProductsValue = response.data.showUsedProductsValue === true
      viewSettings.value.showRealEventDuration = response.data.showRealEventDuration === true
      viewSettings.value.portalPrepaymentEnabled = response.data.portalPrepaymentEnabled === true
      viewSettings.value.showProductStoreLink = response.data.showProductStoreLink === true

      // Integrations
      integrations.value.beautyDoc.enabled = response.data.integrations?.beautydoc?.enabled === true
      integrations.value.beautyDoc.showBlockade = response.data.integrations?.beautydoc?.showBlockade === true
      integrations.value.wealthon.enabled = response.data.integrations?.wealthon?.enabled === true
      integrations.value.wealthon.showBlockade = response.data.integrations?.wealthon?.showBlockade === true

      // Terms and Agreement
      terms.value = response.data.terms
      agreement.value = response.data.agreement

      // Deleted employees
      unavailableEmployees.value = response.data.unavailableEmployees

      // Holidays
      if(response.data.holidays) holidaysArray.value = response.data.holidays

      loyaltyProgramSettings.value = response.data.loyaltyProgram

      hoursRange.value = Array.from(
        { length: parseInt(calendarTimeTo.value) - parseInt(calendarTimeFrom.value) + 1 },
        (_, i) => parseInt(calendarTimeFrom.value) + i,
      )

      paymentMethods.value = [
        eventStore.emptyPrepaymentMethod,
        ...Object.values(response.data.paymentMethods),
      ]

      const emptyVoucher = {
        checkStatus: 0, //0 - to check, 1 - checking, 2 - correct, 3 - incorrect
        statusMessage: '',
        identifier: '',
      }
      //BON
      let voucherPaymentMethod = paymentMethods.value.find(m => m.id === 7)
      if(voucherPaymentMethod) voucherPaymentMethod.voucher = emptyVoucher
      //KARNET
      let passPaymentMethod = paymentMethods.value.find(m => m.id === 9)
      if(passPaymentMethod) passPaymentMethod.voucher = emptyVoucher

      defaultPaymentMethod.value = paymentMethods.value.length > 0 ? paymentMethods.value[0] : null
      //prepaymentEnabled.value = response.data.prepaymentEnabled
      prepaymentSettings.value.enabled = response.data.prepaymentEnabled
      prepaymentSettings.value.defaultAmount = response.data.prepaymentDefaultAmount
      prepaymentSettings.value.defaultType = response.data.prepaymentDefaultType

      clientGroups.value = response.data.clientGroups
      reasonsForEventCancellation.value = response.data.cancelReasons?.map((text, id) => ({ id, text }))

      //MENU ITEMS
      menuItems.value = response.data.menu

      isLoading.value = false
      settingsLoaded.value = true

      return response.data

    } catch (error) {
      settingsLoaded.value = false
      console.error('ERROR fetching settings', error)
    }

    return []
  }

  const currency = ref('zł') // Default zł
  const smsDomesticPrice = ref('')
  const smsInternationalPrice = ref('')

  const availableCalendars = ref([])
  const employeesCalendars = ref([]) // All employees e.g receptionists.
  const calendarEmployeesCalendars = ref([]) // Employees visible on calendar
  const resourcesCalendars = ref([]) //non employees
  const unavailableEmployees = ref([]) // Deleted employees
  const initialView = useStorage('initialView')
  const showCanceledEvents = useStorage('showCanceledEvents') // B: because it has no default value, it always will be string type or undefined (undefined type)
  const customerViewCalendarMode = useStorage('customerViewCalendarMode', false)
  const currentCalendarViewType = ref('')
  const prevCalendarViewType = ref('')
  const isEmployeeWeekView = ref(false) // Flag to tell if we are in single employee week mode

  const selectedCalendars = ref([])
  const selectedCalendarsChanged = ref(false)
  const saveSelectedCalendarsToStorage = () => {
    const user = userStore.currentUser
    if (!user?.ekey) return
    const storageKey = `${user.ekey}_selectedCalendars`
    localStorage.setItem(storageKey, JSON.stringify(selectedCalendars.value))
    selectedCalendarsChanged.value = false
  }

  const restoreSelectedCalendarsFromStorage = () => {
    const saved = localStorage.getItem(`${userStore.currentUser?.ekey}_selectedCalendars`)
    const availableIds = availableCalendars.value.filter(calendar => calendar.showOnCalendar).map(calendar => calendar.id)
    selectedCalendars.value = saved ? JSON.parse(saved).filter(id => availableIds.includes(id)) : availableIds
    selectedCalendarsChanged.value = false
  }

  const calendarTimeFrom = ref('08:00')
  const calendarTimeTo = ref('20:00')
  const defaultSlotDuration = ref('00:15:00')
  const defaultSlotDurationInMinutes = ref(15)

  // Default settings for loyalty program
  const loyaltyProgramSettings = ref({
    enabled: false,
    loyaltyPointsForService: 1,
    loyaltyPointsForProduct: 1,
    loyaltyPointsForVoucher: 1,
  })

  const paymentMethods = ref([])
  const defaultPaymentMethod = ref(null) // Default payment method
  // const prepaymentEnabled = ref(false)
  // Used for storing prepayment settings
  const prepaymentSettings = ref({
    enabled: true,
    defaultAmount: 0,
    defaultType: 1, // 1 solid, 2 percent
  })

  // Fetch employee and resources calendars (update selectedCalendars if needed)
  const updateCalendarEmployeesAndResources = async (updateSelectedCalendars = true) => {
    await fetchEmployeesAndResources().then((data) => {
      if(updateSelectedCalendars) {
        availableCalendars.value = data
        restoreSelectedCalendarsFromStorage()
      }
    })
  }

  // Getters
  const calendarColorsObject = computed(() => {
    return availableCalendars.value.reduce((acc, calendar) => {
      acc[calendar.id] = calendar.color


      return acc
    }, {})
  })

  // Actions
  async function fetchResources() {
    return availableCalendars.value.filter(calendar =>
      selectedCalendars.value.includes(calendar.id) && calendar.showOnCalendar,
    )
  }

  async function fetchEvents(info) {
    await nextTick() //B: dont remove this from here ever!
    let startDateObject = info.start
    let endDateObject = info.end
    //if(currentCalendarViewType.value === 'resourceTimeGridDay') endDateObject = startDateObject // If its day view we need only 1 day window span
    if(calendarApi.value?.view?.type === 'resourceTimeGridDay') endDateObject = startDateObject // If its day view we need only 1 day window span
    try {
      const formData = {
        start: extractDate(startDateObject, "-"),
        end: extractDate(endDateObject, "-"),
        //view: ({ resourceTimeGridDay: 'recepcja', timeGridWeek: 'agendaWeek', dayGridMonth: 'month' })[currentCalendarViewType.value] || 'recepcja',
        view: ({ resourceTimeGridDay: 'recepcja', timeGridWeek: 'agendaWeek', dayGridMonth: 'month' })[calendarApi.value?.view?.type] || 'recepcja',
        eventId: eventStore.allowSave && !eventStore.isSaving ? eventStore.selectedEvent.id : null, // Needed while changing date on FC and event is selected
        showCanceledEvents: showCanceledEvents.value === "true", //
        selectedCalendars: selectedCalendars.value,
      }

      const data = await $backend(`/events/index`, {
        method: 'POST',
        body: formData,
      })

      fetchedEvents.value = []

      return {
        ...data,
        events: data?.events?.map(event => {
          //if(event.id !== 'businessHours') console.log('event', event)

          // Used for eg. showing that customer have other visits in salon on current day
          if(event.id !== 'businessHours') fetchedEvents.value.push({
            id: event.id,
            isResource: event.isResource,
            resourceId: event.resourceId,
            editable: event.editable,
            status: Number(event.status),
            salonerClientId: event.salonerClientId,
            start: event.start,
            end: event.end,
          })

          return {
            ...event,
            //durationTime: event.duration, //B: duration prop from response is removed by FC so we cast it to durationTime
            dynamicId: generateUniqueId(16),
          }
        }),
      }
    } catch (error) {}

    return {}
  }

  async function fetchEvent(eventId) {
    return await $backend(`/events/show/${eventId}`, {
      method: 'GET',
    })
  }

  async function addEvent(event) {
    await $backend('/apps/calendar', {
      method: 'POST',
      body: event,
    })
  }

  async function updateEvent(event) {
    return await $backend(`/apps/calendar/${event.id}`, {
      method: 'PUT',
      body: event,
    })
  }

  async function removeEvent(eventId) {
    return await $backend(`/apps/calendar/${eventId}`, {
      method: 'DELETE',
    })
  }

  async function restoreVisit(event) {
    return await $backend(`events/restore`, {
      method: 'POST',
      body: event,
    })
  }

  const getResourceName = (resourceId) => {
    return resourcesCalendars.value.find(r => r.id === resourceId)?.title || ''
  }

  const getEmployeeById = (id) => {
    if (!id) return null

    const employee = availableCalendars.value.find(employee => employee.id === id)
    if (employee) {
      return {
        ...employee,
        initials: getInitials(employee.title), // Add initials to the employee object
        color: employee.color,
      }
    }

    return null
  }

  const getCoupons = async () => {
    try {
      const response = await $backend(`/loyaltyProgram/getCoupons`, {
        method: 'GET',
      })
      console.log('getCustomerAwards response', response)
      if(response.success) {
        availableCoupons.value = response.data.coupons || []

        return response.data || []
      }
    } catch (error) {
      console.error('Error fetching customer awards:', error)
    }

    return []
  }

  // Update employee color theme
  const updateCalendarColor = async (id, color, type) => {
    // "id": "hcsgpbsqsvrc", employee ekey or resource id
    // "color": "#34ebd2", color in hex
    // "type": "users" users or resources
    try {
      return await $backend(`calendar/updateResourceColor`, {
        method: 'POST',
        body: {
          id: id,
          color: color,
          type: type,
        },
      })
    }
    catch (error) {
      console.error('Error fetching calendar colors:', error)
    }

    return {}
  }

  const refCalendar = ref()
  const calendarApi = ref(null)
  const eventIdToBeOpened = ref(null)

  const tooltipVisible = ref(false)
  const tooltipTarget = ref(null)
  const tooltipText = ref('')

  // Display event box from anywhere,
  const displayElementOnCalendar = async (element, date, closeOtherModals = true) => {
    if (!element || !element.id) {
      showToast(t('invalid_visit_data'), 'error')

      return
    }

    if (element.type === 'events'){
      if(closeOtherModals) {
        modalStore.resetModals()
      } else {
        modalStore.closeModal("calendarEvent")
        modalStore.closeModal("calendarSales")
      }

      await eventStore.resetSelectedEvent() // Reset selected event in store

      const visitDate = new Date(date)

      if (route.path === '/calendar') {
        // We are already on calendar page
        const currentCalendarDate = calendarApi.value.getDate()

        const viewType = calendarApi.value?.view?.type
        if(viewType === 'resourceTimeGridDay'){
          if (currentCalendarDate.toDateString() !== visitDate.toDateString()) {
            // We are on another date, so navigate to the visit date
            eventIdToBeOpened.value = element.id
            calendarApi.value.gotoDate(visitDate)
          } else {
            // We are on same date, try to find and click the event
            calendarApi.value.trigger('eventClick', {
              event: { id: element.id, extendedProps: { simulated: true } },
            })
          }
        } else { // week or month view
          const { currentStart, currentEnd } = calendarApi.value.view
          const visitTime = visitDate.getTime()
          const startTime = currentStart.getTime()
          const endTime = currentEnd.getTime()

          const isInRange = visitTime >= startTime && visitTime < endTime

          if (!isInRange) {
            eventIdToBeOpened.value = element.id
            calendarApi.value.gotoDate(visitDate)
          } else {
            calendarApi.value.trigger('eventClick', {
              event: { id: element.id, extendedProps: { simulated: true } },
            })
          }
        }


      } else {
        // We are NOT on calendar page, navigate there
        eventIdToBeOpened.value = element.id
        const dateStr = extractDate(visitDate, '-')
        router.push(`/calendar?date=${dateStr}`)
      }
    } else {
      window.open(
        `${import.meta.env.VITE_SALONER10_URL}panel/calendar/productSelling/${element.id}/`,
        '_blank',
        'noopener',
      )
    }
  }


  return {
    isLoading,
    refCalendar,
    calendarApi,
    eventIdToBeOpened,
    availableCalendars,
    selectedCalendars,
    calendarColorsObject,
    employeesCalendars,
    calendarEmployeesCalendars,
    resourcesCalendars,
    unavailableEmployees,
    showCanceledEvents,
    initialView,
    customerViewCalendarMode,
    currentCalendarViewType,
    isEmployeeWeekView,
    prevCalendarViewType,
    defaultSlotDuration,
    defaultSlotDurationInMinutes,
    calendarTimeFrom,
    calendarTimeTo,
    paymentMethods,
    defaultPaymentMethod,
    prepaymentSettings,
    clientGroups,
    tooltipVisible,
    tooltipTarget,
    tooltipText,
    currency,
    smsDomesticPrice,
    smsInternationalPrice,
    reasonsForEventCancellation,
    loyaltyProgramSettings,
    availableCoupons,
    hoursRange,
    viewSettings,
    integrations,
    fetchedEvents,
    menuItems,
    selectedCalendarsChanged,
    holidaysArray,
    terms,
    agreement,
    saveSelectedCalendarsToStorage,
    restoreSelectedCalendarsFromStorage,
    getResourceName,
    fetchResources,
    fetchEvents,
    fetchEvent,
    addEvent,
    updateEvent,
    removeEvent,
    getEmployeeById,
    fetchCalendarSettings,
    getCoupons,
    updateCalendarColor,
    displayElementOnCalendar,
    fetchEmployeesAndResources,
    updateCalendarEmployeesAndResources,
    restoreVisit,
    ...stackedActions,
  }
})
